import { Alert, Text, AppShellMain, Box, Affix, Transition, ActionIcon, Group, AppShellNavbar, Flex, AppShell, Button, Tooltip, Badge, Switch } from "@mantine/core"
import { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react"
import { GameSubQuery, PlayerSubQuery, Season, Stat, VideoEvent } from "../client"
import { getGamePBPVideos, getBatchGamePBPVideos } from "../api/Games"
import { getPlayerPBPVideos, getBatchPlayerPBPVideos, getPlayers } from "../api/Players"
import VideoDisplay from "../components/VideoDisplay/VideoDisplay"
import { IconAdjustmentsHorizontal, IconArrowDown, IconArrowUp, IconFilter, IconInfoCircle, IconLayoutSidebarLeftCollapse, IconSearch, IconX } from "@tabler/icons-react"
import InfiniteScroll from "react-infinite-scroll-component"
import { createSearchParams, useNavigate } from "react-router-dom"
import { LEAGUE, nbaTeamsMap, wnbaTeamsMap } from "../models/NBA"
import { StatSchema } from "../client/schemas.gen"
import { getGameString, sortVideos, urlSafeBase64Encode } from "../utils/utils"
import { MAX_STACK_SIZE, StackedGameQuery, StackedPlayerQuery, StackedQuery, areQueriesEquivalent } from "../models/QueryStack"
import { NBAInsightsContext } from "../context/global-state"
import { useDisclosure, useMediaQuery } from "@mantine/hooks"
import { useAuth0 } from "@auth0/auth0-react"
import { NBAInsightsHeader } from "../components/Header/Header"
import SearchForm from "../components/SearchForm/SearchForm"
import { PlayerSearchData, GameSearchData, VideoSearchRanges } from "../models/SearchData"
import { getTeams } from "../api/Teams"
import { useLeague } from "../context/LeagueContext"
import PageLoader from "../components/Common/PageLoader"
import { useSearchUrlState } from "../hooks/useSearchUrlState"
import { useHideOutcomes } from "../hooks/useHideOutcomes"
import { QueryStackChips } from "../components/QueryStack"

const tmpNbaPlayerExclusionList = ['Dee Brown', 'Charlie Davis', 'Johnny Davis', 'Mark Davis', 'Bob Duffy', 'Mike Dunleavy', 'Patrick Ewing', 'Matt Guokas', 'Cedric Henderson', 'Gerald Henderson', 'Mike James', 'Chris Johnson', 'David Johnson', 'Eddie Johnson', 'George Johnson', 'George Johnson', 'Ken Johnson', 'Larry Johnson', 'Bobby Jones', 'Charles Jones', 'Mark Jones', 'Willie Jones', 'George King', 'Tony Mitchell', 'Jim Paxson', 'Glen Rice', 'Walker Russell', 'Bill Smith', 'Charles Smith', 'Charles Smith', 'Chris Smith', 'Greg Smith', 'Michael Smith', 'Steven Smith', 'Jack Turner', 'Brandon Williams', 'Marcus Williams', 'Nate Williams', 'Reggie Williams', 'Chris Wright']
const tmpWnbaPlayerExclusionList = ['Michelle Campbell', 'Korie Hlede', 'Claudia Neves']


const Search: React.FC = () => {
  // Layout info
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true)
  const isSmallScreen = useMediaQuery('(max-width: 768px)');

  const { isSearchOpen, games, nbaPlayers, wnbaPlayers, setNbaPlayers, setWnbaPlayers, setNbaTeams, setWnbaTeams, setIsSearchOpen, setSearchHistory } = useContext(NBAInsightsContext)
  const { isLoading } = useAuth0()

  const handleOpenFilters = () => {
    if (isSmallScreen || window.innerWidth < 1024) {
      setIsSearchOpen(true);
    } else {
      toggleDesktop();
    }
  };

  const isFilterClosed = (isSmallScreen || window.innerWidth < 1024) ? !isSearchOpen : !desktopOpened;

  // scroll to top
  const [scrollIsVisible, setScrollIsVisible] = useState(false);

  // Navigate
  const navigate = useNavigate();
  const {
    league: contextLeague,
    setLeague,
    searchCache,
    clearSearchSnapshot,
    mergeSearchResults,
  } = useLeague()
  const [hideOutcomes, setHideOutcomes] = useHideOutcomes()

  const {
    params,
    setParams,
    searchType,
    league,
    player,
    game,
    lastN,
    stats,
    seasons,
    seasonType,
    opponent,
    dateFrom,
    dateTo,
    periods,
    order,
    matchedPlayer,
    resolvedPlayerId,
    resolvedPlayerIds,
    playerSearchData,
    gameSearchData,
    activeFilterCount,
    searchSummaryLabel,
    fullSearchSummaryLabel,
    isMultiQuery,
    stackedQueries,
    aggregatedPlayerIds,
    setQueryStack,
    removeQueryFromStack,
  } = useSearchUrlState({
    contextLeague,
    nbaPlayers,
    wnbaPlayers,
    games,
    searchCache,
  });

  const [stagedQueries, setStagedQueries] = useState<StackedQuery[]>(stackedQueries)
  const [editingQueryId, setEditingQueryId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<string | null>(
    searchType === "game" || gameSearchData ? "games" : "players"
  )

  useEffect(() => {
    setStagedQueries(stackedQueries)
  }, [stackedQueries])

  const currentQuery = params.toString()
  const activeSnapshot = searchCache[league]
  const isCacheMatch = Boolean(
    activeSnapshot &&
    activeSnapshot.searchParamsString === currentQuery &&
    (searchType === "player" || searchType === "game")
  )
  const videos = useMemo(() => (isCacheMatch && activeSnapshot ? activeSnapshot.videos : []), [isCacheMatch, activeSnapshot])
  const cursor = isCacheMatch && activeSnapshot ? activeSnapshot.cursor : null
  const hasMore = isCacheMatch && activeSnapshot ? activeSnapshot.hasMore : false

  // Search variables
  const limit = 15
  const [loading, setLoading] = useState<boolean>(false)
  const [searchDataLoading, setSearchDataLoading] = useState<boolean>(true)
  const [searchError, setSearchError] = useState<string | null>(null)

  const isPlayerSearchResolving = Boolean(
    searchType === "player" && !isMultiQuery && player && (!resolvedPlayerId ? searchDataLoading : true)
  )
  const isGameSearch = searchType === "game" && !isMultiQuery
  const isPendingSearch = Boolean(
    (isPlayerSearchResolving || isGameSearch || isMultiQuery) &&
    (!isCacheMatch || !activeSnapshot) &&
    !searchError
  )
  const showLoader = (loading || isPendingSearch) && !searchError

  // Sorting variables
  const desc = order === "desc"
  const sortedVideos = useMemo(() => {
    return sortVideos(videos, desc)
  }, [videos, desc])

  const handleToggleSort = () => {
    const nextOrder = desc ? "asc" : "desc"
    const nextParams = new URLSearchParams(params)
    if (nextOrder === "desc") {
      nextParams.delete("order")
    } else {
      nextParams.set("order", nextOrder)
    }
    setParams(nextParams)
  }

  // scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const abortControllerRef = useRef<AbortController | null>(null)
  const lastFetchedQueryRef = useRef<string>("")

  const fetchData = useCallback(async (searchType: string | null, currentCursor: string | null) => {
    if (!searchType) return
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    setLoading(true)
    setSearchError(null)

    if (currentCursor === null) {
      scrollToTop()
    }
    
    try {
      let result = null
      if (searchType === "player") {
        if (isMultiQuery) {
          const playerQueries: PlayerSubQuery[] = stackedQueries
            .filter((q): q is StackedPlayerQuery => q.type === "player")
            .map((q) => {
              const availableSeasons = VideoSearchRanges[league]?.[q.seasonType] || VideoSearchRanges[league]["Regular Season"] || []
              const validSeasons = q.seasons.filter((s) => availableSeasons.includes(s))
              const querySeasons = validSeasons.length > 0 ? validSeasons : [availableSeasons[0] || (league === LEAGUE.WNBA ? ("2026" as Season) : ("2026-27" as Season))]

              const validStats = q.categories.filter((s) => Object.values(StatSchema.enum).includes(s))
              const queryStats = validStats.length > 0 ? validStats : [StatSchema.enum.find((v) => v === "PTS") || ("PTS" as Stat)]

              return {
                playerId: q.player.id,
                contexts: queryStats,
                seasons: querySeasons,
                seasonType: q.seasonType,
                opponentTeamId: q.oppTeamId && q.oppTeamId !== "0" ? q.oppTeamId : undefined,
                lastNGames: q.lastNGames && q.lastNGames !== "0" ? Number(q.lastNGames) : undefined,
                dateFrom: q.dateFrom || undefined,
                dateTo: q.dateTo || undefined,
                periods: q.periods || [],
              }
            })

          if (playerQueries.length === 0) return

          result = await getBatchPlayerPBPVideos(
            league,
            playerQueries,
            currentCursor,
            limit,
            order,
            controller.signal
          )
        } else {
          const queryPlayerId = resolvedPlayerId
          if (!queryPlayerId) {
            return
          }

          const availableSeasons = VideoSearchRanges[league]?.[seasonType] || VideoSearchRanges[league]["Regular Season"] || []
          const validSeasons = seasons.filter((s) => availableSeasons.includes(s))
          const querySeasons = validSeasons.length > 0 ? validSeasons : [availableSeasons[0] || (league === LEAGUE.WNBA ? ("2026" as Season) : ("2026-27" as Season))]

          const validStats = stats.filter((s) => Object.values(StatSchema.enum).includes(s))
          const queryStats = validStats.length > 0 ? validStats : [StatSchema.enum.find((v) => v === "PTS") || ("PTS" as Stat)]

          result = await getPlayerPBPVideos(
            league,
            queryPlayerId,
            lastN,
            queryStats,
            querySeasons,
            seasonType,
            opponent,
            currentCursor,
            limit,
            dateFrom,
            dateTo,
            periods,
            order,
            controller.signal
          )
        }
      }

      if (searchType === "game") {
        if (isMultiQuery) {
          const gameQueries: GameSubQuery[] = stackedQueries
            .filter((q): q is StackedGameQuery => q.type === "game")
            .map((q) => ({
              gameId: q.game.id,
              contexts: q.categories.length > 0 ? q.categories : undefined,
              seasonType: q.seasonType,
              players: q.players && q.players.length > 0 ? q.players.map((p) => p.id) : undefined,
              periods: q.periods || [],
            }))

          if (gameQueries.length === 0) return

          result = await getBatchGamePBPVideos(
            league,
            gameQueries,
            currentCursor,
            limit,
            order,
            controller.signal
          )
        } else {
          result = await getGamePBPVideos(
            game,
            league,
            seasonType,
            stats,
            currentCursor,
            limit,
            periods,
            resolvedPlayerIds,
            order,
            controller.signal
          )
        }
      }

      if (controller.signal.aborted) {
        return
      }

      if (result && result.items) {
        setSearchError(null)
        mergeSearchResults(
          league,
          currentQuery,
          result.items,
          result.nextCursor ?? null,
          result.total ?? null,
          currentCursor === null,
          limit
        )
      } else if (result === null) {
        setSearchError("Unable to load video highlights. Please try again.")
      }
    } catch {
      if (!controller.signal.aborted) {
        setSearchError("Unable to load video highlights. Please try again.")
      }
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false)
      }
    }
  }, [game, lastN, league, mergeSearchResults, opponent, currentQuery, resolvedPlayerId, resolvedPlayerIds, seasonType, seasons, stats, dateFrom, dateTo, periods, order, isMultiQuery, stackedQueries])

  useEffect(() => {
    const urlLeague = params.get("league")?.toLowerCase();
    if (urlLeague && urlLeague !== league) {
      return;
    }

    if (searchType === "player" || searchType === "game") {
      if (searchType === "player" && !isMultiQuery) {
        if (!player) return
        if (!resolvedPlayerId) {
          if (searchDataLoading) {
            setLoading(true)
            return
          }
          clearSearchSnapshot(league)
          setLoading(false)
          return
        }
      }

      if (isMultiQuery && stackedQueries.length === 0) {
        return;
      }

      // Check if cache already matches currentQuery
      if (isCacheMatch && activeSnapshot) {
        lastFetchedQueryRef.current = currentQuery
        setLoading(false)
        return
      }

      if (currentQuery && currentQuery === lastFetchedQueryRef.current) {
        return
      }
      lastFetchedQueryRef.current = currentQuery
      fetchData(searchType, null)
    } else {
      lastFetchedQueryRef.current = ""
    }
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [clearSearchSnapshot, params, searchType, player, resolvedPlayerId, searchDataLoading, league, isCacheMatch, activeSnapshot, currentQuery, fetchData, isMultiQuery, stackedQueries.length])

  // Scroll to top check
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setScrollIsVisible(true);
      } else {
        setScrollIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => {
      window.removeEventListener('scroll', toggleVisibility)
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [league]);

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const [nbaPlayerList, wnbaPlayerList, nbaTeamList, wnbaTeamList] = await Promise.all([
          getPlayers(LEAGUE.NBA),
          getPlayers(LEAGUE.WNBA),
          getTeams(LEAGUE.NBA),
          getTeams(LEAGUE.WNBA),
        ])
        if (!active) return
        setNbaPlayers(nbaPlayerList.filter(p => !tmpNbaPlayerExclusionList.includes(p.fullName)))
        setWnbaPlayers(wnbaPlayerList.filter(p => !tmpWnbaPlayerExclusionList.includes(p.fullName)))
        setNbaTeams(nbaTeamList)
        setWnbaTeams(wnbaTeamList)
      } finally {
        if (active) {
          setSearchDataLoading(false)
        }
      }
    })()
    return () => { active = false }
  }, [setNbaPlayers, setNbaTeams, setWnbaPlayers, setWnbaTeams])

  const next = () => {
    if (!cursor) return
    if (searchType) fetchData(searchType, cursor)
  }


  // Handle select on video clip
  const handleOnSelect = async (video: VideoEvent) => {
    const hash = urlSafeBase64Encode(`${video.gameId}:${video.eventNumber}`)
    const from = encodeURIComponent(params.toString())
    navigate(`/video/${hash}?from=${from}&sort=${desc ? "desc" : "asc"}`, { state: { cursor }})
  }

  // Handle player search event
  const handlePlayerSearch = async (data: PlayerSearchData) => {  
    if (!data?.player?.id) return
    setEditingQueryId(null)
    setStagedQueries([])
    setLeague(data.league)
    const searchParamsObj: Record<string, string> = {
      type: "player",
      league: data.league,
      player: data.player.id,
      lastN: data.lastNGames,
      stats: data.categories.join(','),
      seasons: data.seasons.join(','),
      seasonType: data.seasonType,
      opponent: data.oppTeamId,
      periods: data.periods.join(','),
    }
    if (data.dateFrom) {
      searchParamsObj.dateFrom = data.dateFrom
    }
    if (data.dateTo) {
      searchParamsObj.dateTo = data.dateTo
    }
    const search = `/search?${createSearchParams(searchParamsObj)}`
    const teamName = (nbaTeamsMap[data.oppTeamId]?.nickname )|| wnbaTeamsMap[data.oppTeamId]?.nickname || ""
    const dateRangeStr = (data.dateFrom || data.dateTo) ? ` - ${data.dateFrom || ''} to ${data.dateTo || ''}` : ''
    setSearchHistory(prev => [...prev, { title: `Player: ${data.player.fullName}${teamName && ' vs ' + teamName + ' '}- ${data.seasons.map(s => s)} - ${data.categories.map(d => d)}${dateRangeStr}`, url: search, pos: prev.length}])
    setIsSearchOpen(false)
    navigate(search)
  }
  

  // Handle game search event
  const handleGameSearch = async (data: GameSearchData) => {
    if (!data?.game?.id) return
    setEditingQueryId(null)
    setStagedQueries([])
    setLeague(data.league)
    const searchParamsObj: Record<string, string> = {
      type: "game",
      league: data.league,
      game: data.game.id,
      stats: data.categories.join(','),
      seasonType: data.seasonType,
      singleSeason: data.singleSeason,
      periods: data.periods.join(','),
    }
    if (data.players && data.players.length > 0) {
      searchParamsObj.players = data.players.map(p => p.id).join(',')
    }
    const search = `/search?${createSearchParams(searchParamsObj)}`
    const playersTitle = data.players && data.players.length > 0
      ? ` - ${data.players.map(p => p.fullName).join(', ')}`
      : ''
    setSearchHistory(prev => [...prev, { title: `Game: ${getGameString(data.game)}${playersTitle}`, url: search, pos: prev.length }])
    setIsSearchOpen(false)
    navigate(search)
  }

  const handleClearSearch = () => {
    setEditingQueryId(null)
    lastFetchedQueryRef.current = ""
    abortControllerRef.current?.abort()
    setSearchError(null)
    clearSearchSnapshot(league)
    setStagedQueries([])
    setParams({ league })
  }

  const handleSelectQuery = (query: StackedQuery) => {
    setEditingQueryId(query.id)
    if (isSmallScreen || window.innerWidth < 1024) {
      setIsSearchOpen(true)
    } else {
      if (!desktopOpened) toggleDesktop()
    }
  }

  const handleUpdateStackPlayer = (data: PlayerSearchData) => {
    if (!editingQueryId || !data?.player?.id) return
    setStagedQueries((prev) =>
      prev.map((q) =>
        q.id === editingQueryId && q.type === "player"
          ? {
              ...data,
              id: q.id,
              type: "player",
            }
          : q
      )
    )
    setEditingQueryId(null)
  }

  const handleUpdateStackGame = (data: GameSearchData) => {
    if (!editingQueryId || !data?.game?.id) return
    setStagedQueries((prev) =>
      prev.map((q) =>
        q.id === editingQueryId && q.type === "game"
          ? {
              ...data,
              id: q.id,
              type: "game",
            }
          : q
      )
    )
    setEditingQueryId(null)
  }

  const handleCancelEdit = () => {
    setEditingQueryId(null)
  }

  const handleAddToStackPlayer = (data: PlayerSearchData) => {
    if (!data?.player?.id) return
    const id = crypto.randomUUID()
    const newQuery: StackedPlayerQuery = {
      ...data,
      id,
      type: "player",
    }

    setStagedQueries((prev) => {
      const filtered = prev.filter((q) => q.type === "player")
      if (filtered.length >= MAX_STACK_SIZE) return prev
      if (filtered.some((q) => areQueriesEquivalent(q, newQuery))) return prev
      return [...filtered, newQuery]
    })
  }

  const handleAddToStackGame = (data: GameSearchData) => {
    if (!data?.game?.id) return
    const id = crypto.randomUUID()
    const newQuery: StackedGameQuery = {
      ...data,
      id,
      type: "game",
    }

    setStagedQueries((prev) => {
      const filtered = prev.filter((q) => q.type === "game")
      if (filtered.length >= MAX_STACK_SIZE) return prev
      if (filtered.some((q) => areQueriesEquivalent(q, newQuery))) return prev
      return [...filtered, newQuery]
    })
  }

  const handleRemoveFromStagedStack = (id: string) => {
    if (editingQueryId === id) {
      setEditingQueryId(null)
    }
    setStagedQueries((prev) => prev.filter((q) => q.id !== id))
  }

  const handleClearStagedStack = () => {
    setEditingQueryId(null)
    setStagedQueries([])
    if (isMultiQuery) {
      handleClearSearch()
    }
  }

  const handleExecuteStack = () => {
    if (stagedQueries.length === 0) return
    setLeague(stagedQueries[0].league)
    setQueryStack(stagedQueries)

    const titles = stagedQueries.map((q) => {
      if (q.type === "player") {
        const statStr = q.categories.length > 0 ? ` (${q.categories.join(", ")})` : ""
        return `${q.player.fullName}${statStr}`
      } else {
        return getGameString(q.game)
      }
    })
    setSearchHistory((prev) => [
      ...prev,
      {
        title: `Multi-Query: ${titles.join(" + ")}`,
        url: window.location.pathname + window.location.search,
        pos: prev.length,
      },
    ])

    if (isSmallScreen || window.innerWidth < 1024) {
      setIsSearchOpen(false)
    }
  }

  const showQueryChips = isMultiQuery || stackedQueries.length > 1
  const subQueryChips = showQueryChips ? (
    <QueryStackChips
      queries={stackedQueries}
      editingQueryId={editingQueryId}
      onSelectQuery={handleSelectQuery}
      onRemoveQuery={(id) => {
        if (editingQueryId === id) {
          setEditingQueryId(null)
        }
        removeQueryFromStack(id)
      }}
    />
  ) : null

  if (isLoading) {
    return <PageLoader />
  }


  const affixContent = (
    <Transition transition="slide-up" mounted={scrollIsVisible}>
      {(transitionStyles) => (
        <ActionIcon
          color="#5c5c5cff"
          size="lg"
          radius="xl"
          variant="filled"
          style={transitionStyles}
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <IconArrowUp color="white" size="1.125rem" />
        </ActionIcon>
      )}
    </Transition>
  )

  const errorAlert = searchError ? (
    <Alert variant="light" color="red" title="Unable to load clips" icon={<IconInfoCircle />}>
      <Group justify="space-between" align="center">
        <Text size="sm">{searchError}</Text>
        <Button
          size="xs"
          variant="subtle"
          color="red"
          onClick={() => fetchData(searchType, cursor)}
        >
          Retry
        </Button>
      </Group>
    </Alert>
  ) : null

  return (
    <AppShell
      padding={"md"}
      header={{ height: 64 }}
      navbar={{ width: 375, breakpoint: 1024, collapsed: { mobile: !isSearchOpen, desktop: !desktopOpened } }}
    > 
      <NBAInsightsHeader />

      <AppShellNavbar p="sm" px={8}>
        <Flex justify="space-between" align="center" px="xs" pb="xs" mb="xs" style={{ borderBottom: '1px solid #2e2e2e' }}>
          <Group gap="xs">
            <IconFilter size={17} color="#aaa" />
            <Text fw={700} fz="sm">Search Filters</Text>
          </Group>
          {/* Mobile Close Drawer Button */}
          <ActionIcon
            style={{ display: (isSmallScreen || window.innerWidth < 1024) ? 'flex' : 'none' }}
            variant="subtle"
            color="gray"
            size="sm"
            onClick={() => setIsSearchOpen(false)}
            aria-label="Close filters"
          >
            <IconX size={18} />
          </ActionIcon>
          {/* Desktop Collapse Button */}
          <Tooltip label="Collapse sidebar" withArrow>
            <ActionIcon
              style={{ display: !(isSmallScreen || window.innerWidth < 1024) ? 'flex' : 'none' }}
              variant="subtle"
              color="gray"
              size="sm"
              onClick={toggleDesktop}
              aria-label="Collapse sidebar"
            >
              <IconLayoutSidebarLeftCollapse size={18} />
            </ActionIcon>
          </Tooltip>
        </Flex>
        <Box style={{ flex: 1, overflowY: 'auto' }}>
          <SearchForm
            playerSearchData={playerSearchData}
            gameSearchData={gameSearchData}
            activeSearchType={searchType}
            handleOnPlayerSearch={handlePlayerSearch}
            handleOnGameSearch={handleGameSearch}
            isMultiQuery={isMultiQuery}
            stackedQueries={stagedQueries}
            editingQueryId={editingQueryId}
            onSelectQuery={handleSelectQuery}
            onAddToStackPlayer={handleAddToStackPlayer}
            onAddToStackGame={handleAddToStackGame}
            onUpdateInStackPlayer={handleUpdateStackPlayer}
            onUpdateInStackGame={handleUpdateStackGame}
            onCancelEdit={handleCancelEdit}
            onRemoveFromStack={handleRemoveFromStagedStack}
            onClearStack={handleClearStagedStack}
            onExecuteStack={handleExecuteStack}
            isStackLoading={loading}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </Box>
        <Box p="xs" px="md" style={{ borderTop: '1px solid #282828', backgroundColor: '#181818' }}>
          {editingQueryId ? (
            <Button
              w="100%"
              size="sm"
              radius="md"
              type="submit"
              form="search-filter-form"
              disabled={loading || searchDataLoading}
            >
              Update in Stack
            </Button>
          ) : stagedQueries.length > 0 ? (
            <Button
              w="100%"
              size="sm"
              radius="md"
              color="accent"
              leftSection={<IconSearch size={15} />}
              loading={loading}
              onClick={handleExecuteStack}
            >
              Search Feed ({stagedQueries.length} {stagedQueries.length === 1 ? "query" : "queries"})
            </Button>
          ) : (
            <Button
              w="100%"
              size="sm"
              radius="md"
              type="submit"
              form="search-filter-form"
              loading={loading}
              disabled={loading || searchDataLoading}
              leftSection={<IconSearch size={15} />}
            >
              {activeTab === "games" ? "Get clips" : "Search"}
            </Button>
          )}
        </Box>
      </AppShellNavbar>

      <AppShellMain>
        {/* Mobile Dedicated Sticky Search Subheader Bar */}
        <Box
          hiddenFrom="sm"
          style={{
            position: 'sticky',
            top: 64,
            zIndex: 10,
            backgroundColor: '#161616',
            borderBottom: '1px solid #282828',
            padding: '8px 12px',
            margin: '-16px -16px 12px -16px',
          }}
        >
          <Flex justify="space-between" align="center">
            <Group gap="xs" style={{ flex: 1, overflow: 'hidden' }}>
              <Button
                size="xs"
                radius="xl"
                variant="light"
                leftSection={<IconAdjustmentsHorizontal size={14} />}
                rightSection={activeFilterCount > 0 ? (
                  <Badge size="xs" variant="filled" circle color="accent">
                    {activeFilterCount}
                  </Badge>
                ) : null}
                onClick={handleOpenFilters}
              >
                {searchType ? "Edit Search" : "Filters"}
              </Button>
              { searchSummaryLabel && (
                <Tooltip label={fullSearchSummaryLabel || searchSummaryLabel} disabled={!fullSearchSummaryLabel}>
                  <Text fz="xs" c="dimmed" truncate style={{ flex: 1 }}>
                    {searchSummaryLabel}
                  </Text>
                </Tooltip>
              )}
            </Group>
            <Group gap="xs" style={{ flexShrink: 0 }}>
              <Switch
                size="xs"
                label="Hide Outcomes"
                checked={hideOutcomes}
                onChange={(e) => setHideOutcomes(e.currentTarget.checked)}
                styles={{
                  label: {
                    fontSize: 11,
                    fontWeight: 600,
                    paddingLeft: 5,
                  },
                }}
              />
              {showLoader ? (
                <Text fz="xs" c="dimmed">Loading clips...</Text>
              ) : (
                <Text fz="xs" c="dimmed">{videos.length} clip{videos.length === 1 ? '' : 's'}</Text>
              )}
              {(videos.length > 0 || searchType) && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="gray"
                  onClick={handleClearSearch}
                >
                  Clear
                </Button>
              )}
              <ActionIcon variant="subtle" color="white" size="sm" onClick={handleToggleSort}>
                {desc ? <IconArrowDown size={14} /> : <IconArrowUp size={14} />}
              </ActionIcon>
            </Group>
          </Flex>
          {subQueryChips && <Box pt={4}>{subQueryChips}</Box>}
        </Box>

        { videos.length === 0 ? (
          showLoader ? (
            <VideoDisplay
              action="add"
              onSelect={handleOnSelect}
              videos={[]}
              targetPlayerId={isMultiQuery ? undefined : (playerSearchData?.player?.id || resolvedPlayerId || params.get('player'))}
              targetPlayerName={isMultiQuery ? undefined : (playerSearchData?.player?.fullName || matchedPlayer?.fullName)}
              targetPlayerIds={isMultiQuery ? aggregatedPlayerIds : resolvedPlayerIds}
              stats={isMultiQuery ? undefined : stats}
              isExpanded={!desktopOpened}
              isLoadingMore={true}
            />
          ) : (
            <Box p={12}>
              {subQueryChips && <Box mb="sm">{subQueryChips}</Box>}
              {isFilterClosed && (
                <Group mb="md" visibleFrom="sm">
                  <Button
                    size="xs"
                    variant="light"
                    radius="xl"
                    leftSection={<IconAdjustmentsHorizontal size={14} />}
                    rightSection={activeFilterCount > 0 ? (
                      <Badge size="xs" variant="filled" circle color="accent">
                        {activeFilterCount}
                      </Badge>
                    ) : null}
                    onClick={handleOpenFilters}
                  >
                    {searchType ? "Edit Search" : "Filters"}
                  </Button>
                </Group>
              )}
              {errorAlert || (
                searchType === "player" && player && !resolvedPlayerId && !searchDataLoading ? (
                  <Alert variant="light" color="red" title="Player Not Found" icon={<IconInfoCircle />}>
                    Could not find player &quot;{player}&quot; in {league}. Please check the spelling or choose a player from the filters.
                  </Alert>
                ) : searchType ? (
                  <Alert variant="light" title="No clips found" icon={<IconInfoCircle />}>
                    No clips found matching your search. Try adjusting your filters or stat categories.
                  </Alert>
                ) : (
                  <Alert variant="light" title="Create a search" icon={<IconInfoCircle />}>
                    Search for any NBA or WNBA player or game to watch highlights.
                  </Alert>
                )
              )}
            </Box>
          )
        ) : (
          <>
            <Group visibleFrom="sm" w={"100%"} justify={"space-between"} pl={20} pr={20} pb={5}>
              <Group gap="sm">
                {isFilterClosed && (
                  <Button
                    size="xs"
                    variant="light"
                    radius="xl"
                    leftSection={<IconAdjustmentsHorizontal size={14} />}
                    rightSection={activeFilterCount > 0 ? (
                      <Badge size="xs" variant="filled" circle color="accent">
                        {activeFilterCount}
                      </Badge>
                    ) : null}
                    onClick={handleOpenFilters}
                  >
                    {searchType ? "Edit Search" : "Filters"}
                  </Button>
                )}
                <Text size="16px">{videos.length} clip{videos.length === 1 ? "" : "s" }</Text>
                <Button
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={handleClearSearch}
                >
                  Clear Results
                </Button>
              </Group>
              <Group gap="md">
                <Switch
                  size="sm"
                  label="Hide Outcomes"
                  checked={hideOutcomes}
                  onChange={(e) => setHideOutcomes(e.currentTarget.checked)}
                />
                <Tooltip label={`Sorting events in ${desc ? "descending" : "ascending"} order`}>
                  <ActionIcon variant='subtle' color='white' onClick={handleToggleSort}>
                    { desc ? <IconArrowDown size={16} /> : <IconArrowUp size={16} /> }
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
            {subQueryChips && <Box px={20} pb={4}>{subQueryChips}</Box>}
            {errorAlert && <Box px={20} pb="sm">{errorAlert}</Box>}
            <InfiniteScroll
              dataLength={videos.length}
              hasMore={hasMore}
              next={next}
              loader={null}
              scrollThreshold={0.8}
              endMessage={<Text ta={"center"} mb={4}>No more videos</Text>}
              style={{"overflow": "hidden"}}
              children={
                <VideoDisplay
                  action="add"
                  onSelect={handleOnSelect}
                  videos={sortedVideos}
                  targetPlayerId={isMultiQuery ? undefined : (playerSearchData?.player?.id || resolvedPlayerId || params.get('player'))}
                  targetPlayerName={isMultiQuery ? undefined : (playerSearchData?.player?.fullName || matchedPlayer?.fullName)}
                  targetPlayerIds={isMultiQuery ? aggregatedPlayerIds : resolvedPlayerIds}
                  stats={isMultiQuery ? undefined : stats}
                  isExpanded={!desktopOpened}
                  isLoadingMore={loading && videos.length > 0}
                />
              }
            />
          </>
        )}

        { isSmallScreen ? 
          <Affix position={{ bottom: 30, right: "45%" }}>
            {affixContent}
          </Affix>
          :
          <Affix position={{ bottom: 30, right: desktopOpened ? "calc(50% - 205px)" : "50%" }}>
            {affixContent}
          </Affix>
        }
      </AppShellMain>
    </AppShell>
  )
}

export default Search